/**
 * Admin → Portfolios → View portfolio: one portfolio, or several side by side with their
 * consolidated Total (HP-C, Pedro 2026-10-19).
 *
 * Pedro: "Some Agreements have more than one Portfolio or Account, and we want to be able to
 * see each Portfolio individually, but also the consolidated view of some or all of their
 * Portfolios … both individual and consolidated data columns visible in the table at the
 * same time." His answers (OPEN_ITEMS HP-C):
 *   Q1 pick any portfolios (the Agreement shortcut comes with the account linking);
 *   Q2 one positions date; a portfolio without positions on it is flagged loudly, with
 *      "Use latest known positions", then shown as not current with the date of its positions;
 *   Q3 any reference currency held that day, at the custodian's own rates;
 *   Q4 sums, weighted average price (quantity × price = value), cost-based average price;
 *   Q5 "-" where a portfolio does not hold a security; Q6 headings grouped by measure;
 *   Q7 Show any of the portfolios' own columns and/or the Total; sort, filter, group
 *      subtotals on every column; Accrued interest and FX for the Total only.
 * Loans (HP-L, Pedro 2026-10-20): Category LOANS lines form their own "Loans" group; the
 * bottom of the table carries a Gross total (loans left out) and a Net total (with them);
 * every % is a share of the Gross value; a cash or loan row shows its account code (the
 * file's Denomination) in the ISIN / account column.
 * The figures follow `utils/portfolioConsolidation` (pure, checked by its .check.mjs); the
 * grid keeps HP-V's shape: full width, compact toolbar, table to the window bottom, column
 * order / widths / freeze / origins, ordered foldable Group by (utils/portfolioGrouping).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, CloseButton, Group, Loader, MultiSelect, Popover,
  SegmentedControl, Select, Stack, Text, Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle, IconInfoCircle, IconChevronDown, IconChevronRight, IconClock, IconFilter, IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { tradesApi } from '../api/trades';

import { classificationApi, type ClassificationScheme } from '../api/classification';
import { portfolioConsolidationsApi, type Consolidation } from '../api/portfolioConsolidations';
import {
  portfoliosApi, splitRef, type ConsolidatedPortfolio, type ConsolidatedRow, type ConsolidatedView,
} from '../api/portfolios';
import { ColumnResizeHandle } from '../components/ColumnResizeHandle';
import { securityLink } from '../utils/securityLink';
import { PortfolioPicker } from '../components/PortfolioPicker';
import { useTradeMode } from './TradeMode';
import { useConsolidationTradeMode } from './ConsolidationTradeMode';
import { RateNotices, sourceWhy } from '../components/RateNotices';
import { ColumnsPicker } from '../components/ColumnsPicker';
import { ORIGINS, ORIGIN_COLOR, ORIGIN_ORDER, type Origin } from '../components/columnOrigin';
import { ordered, useColumnOrder, useColumnWidths, useHeadingDrag } from '../components/gridColumnOrder';
import {
  ColumnMenu, filterActive, passesFilter, type Filters, type Ft, type SortState,
} from '../components/GridColumnMenu';
import {
  SUMMABLE, TOTAL, convert, costUnknown, grossIn, measure, ratesFor, usesSubstitute, type Ctx, type Measure, type Scope,
} from '../utils/portfolioConsolidation';
import {
  allGroupIds, CONSOLIDATED_ACCESS, groupRowBg, groupRows, levelKey, levelOptions, validLevels,
  type GroupNode,
} from '../utils/portfolioGrouping';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const fmt = (v: number | null, digits: number, max = digits) =>
  v == null ? '-' : v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: max });
const amount = (v: number | null) => fmt(v, 2);
const qty = (v: number | null) => fmt(v, 0, 6);
const percent = (v: number | null) => (v == null ? '-' : `${fmt(v, 2)}%`);
/** PF-MAX (Pedro 2026-10-22): "above 20 portfolios, we can only see the consolidated total columns". */
const TOTALS_ONLY_ABOVE = 20;
/** A portfolio's short name for headings and badges: bank mnemonic + 6-digit key (0113). */
const shortLabel = (p: ConsolidatedPortfolio) => `${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_key}`;
/** A security line of the Securities Master that is not Approved (the "Non-approved" pill). */
const notApproved = (r: ConsolidatedRow) => !r.is_cash && !r.is_loan && !!r.security_id && !r.authorized;
const readLocal = <T,>(key: string, fallback: T): T => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : (JSON.parse(v) as T); }
  catch { return fallback; }
};

/** A request to View portfolio from elsewhere on the page: show a portfolio (`ref`), with a draft
 *  open in trade mode (`draft`, TRADE-LIST); or (TRADE-CONS 4b) a run open on its consolidation. */
export interface ViewRequest {
  ref: string; n: number; draft?: string | null;
  run?: { id: string; consolidationId: string; refs: string[] } | null;
}

export function ViewTab({ request, scope, tradable, preferredRef }: {
  /** `draft` (TRADE-LIST): a Trades row opens that draft in trade mode on its portfolio. */
  request?: ViewRequest;
  /** TRADE-1 / TRADES-RM: trade mode on top of the view (Portfolios → View portfolio, for
   *  whoever holds `trades.draft`; not on an agreement's Portfolios tab). */
  tradable?: boolean;
  /** Step 4 (Pedro P4, 2026-10-21): "the read-only view of just that Agreement's Portfolios,
   *  shown as they appear in Portfolios\View Portfolio, with all the view customization". The
   *  picker offers only these, all ticked to start. */
  scope?: { ref: string; label: string }[];
  /** MR-C (0126): an agreement's Preferred Ref Ccy, the Ref Ccy the view opens in when offered. */
  preferredRef?: string;
}) {
  // The chosen portfolios, as refs (bank + number, 0113), and the consolidations ticked (U4 step 2;
  // their portfolios are in `numbers` too).
  const [numbers, setNumbers] = useState<string[]>([]);
  const [consIds, setConsIds] = useState<string[]>([]);
  const [asOf, setAsOf] = useState<string | undefined>(undefined);
  const [view, setView] = useState<string | null>(null);
  const [substitutes, setSubstitutes] = useState<Record<string, string>>({});
  // TOT-DASH: the shown currency, and the fallback rates chosen or typed ("bank:CCY" → choice);
  // for this view only: cleared on a new date or a new choice of portfolios, never stored.
  const [show, setShow] = useState<string | undefined>(undefined);
  const [rateChoices, setRateChoices] = useState<Record<string, string>>({});
  // A "View" button elsewhere on the page hands one portfolio over.
  useEffect(() => {
    // TRADE-CONS 4b: a run opens on its consolidation (its portfolios, the consolidation ticked).
    setLocalRun(null);   // a request from elsewhere on the page replaces a run opened here
    if (request?.run) { setNumbers(request.run.refs); setConsIds([request.run.consolidationId]); setAsOf(undefined); setSubstitutes({}); setRateChoices({}); }
    else if (request?.ref) { setNumbers([request.ref]); setConsIds([]); setAsOf(undefined); setSubstitutes({}); setRateChoices({}); }
  }, [request?.n, request?.ref]); // eslint-disable-line react-hooks/exhaustive-deps
  const scopeKey = (scope ?? []).map((s) => s.ref).join(',');
  useEffect(() => {
    // N4: a draft opened from a Trades row shows its own portfolio, not the whole scope (a run: its consolidation).
    if (scope) {
      setNumbers(request?.run ? request.run.refs : request?.ref ? [request.ref] : scope.map((s) => s.ref));
      setConsIds(request?.run ? [request.run.consolidationId] : []); setAsOf(undefined); setSubstitutes({}); setRateChoices({});
    }
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewsQ = useQuery({ queryKey: ['classification', 'schemes'], queryFn: () => classificationApi.schemes() });
  const q = useQuery({
    queryKey: ['portfolios', 'consolidated', numbers.join(','), asOf ?? 'latest', view ?? 'active',
      JSON.stringify(substitutes), JSON.stringify(rateChoices), show ?? ''],
    queryFn: () => portfoliosApi.consolidated(numbers, { asOf, view, substitutes, rateChoices, show }),
    enabled: numbers.length > 0,
    placeholderData: (prev) => prev,
  });

  // U4 step 2 / TRADE-CONS 4b: exactly one consolidation shown (and nothing else) → traded as one.
  const consQ = useQuery({ queryKey: ['portfolio-consolidations'], queryFn: () => portfolioConsolidationsApi.list() });
  const oneCons = consIds.length === 1 ? (consQ.data?.items ?? []).find((c) => c.id === consIds[0]) : undefined;
  const shownConsolidation = oneCons && oneCons.members.length === numbers.length
    && oneCons.members.every((m) => numbers.includes(m.ref)) ? oneCons : null;

  // TRADE-CONS 4b (smoke 2026-10-30): a run's draft opened on its portfolio links to its run —
  // show the run's consolidation and open the run there (`n` keeps each request distinct).
  const [localRun, setLocalRun] = useState<{ id: string; n: number } | null>(null);
  const openRunHere = async (id: string) => {
    try {
      const run = await tradesApi.runs.get(id);
      setNumbers(run.drafts.map((d) => d.portfolio)); setConsIds([run.consolidation_id]);
      setAsOf(undefined); setSubstitutes({}); setRateChoices({});
      setLocalRun({ id, n: Date.now() });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Run not opened', message: errMsg(e) });
    }
  };

  // No Card around the picker (C-385); one compact toolbar row (HP-V).
  const picker = (
    <PortfolioPicker value={numbers} consolidations={consIds} only={scope} onChange={(ns, cs) => {
      setNumbers(ns);
      setConsIds(cs ?? []);
      setSubstitutes((s) => Object.fromEntries(Object.entries(s).filter(([n]) => ns.includes(n))));
      setRateChoices({});
    }} />
  );
  return (
    <Stack gap="xs">
      {numbers.length > 0 && q.data ? (
        <ConsolidatedGrid c={q.data} views={viewsQ.data ?? []} picker={picker} tradable={tradable} preferredRef={preferredRef}
          consolidation={shownConsolidation}
          openDraft={request?.draft ? { id: request.draft, ref: request.ref, n: request.n } : null}
          openRun={localRun ?? (request?.run ? { id: request.run.id, n: request.n } : null)}
          onOpenRun={(id) => void openRunHere(id)}
          onDate={(d) => { setAsOf(d); setSubstitutes({}); setRateChoices({}); }} onView={setView}
          onSubstitute={(n, d) => setSubstitutes((s) => {
            const x = { ...s };
            if (d) x[n] = d; else delete x[n];
            return x;
          })}
          onShow={(ccy) => setShow(ccy)}
          onRateChoice={(key, choice) => setRateChoices((s) => {
            const x = { ...s };
            if (choice) x[key] = choice; else delete x[key];
            return x;
          })} />
      ) : (
        <>
          {picker}
          {q.isLoading && numbers.length > 0 && <Loader size="sm" />}
          {q.error && <Alert color="red" title="Portfolios not shown">{errMsg(q.error)}</Alert>}
        </>
      )}
    </Stack>
  );
}

// ── columns ──────────────────────────────────────────────────────────────────

/** A column as the Columns list and the order know it (one per measure, Q6/Q7). */
interface BaseCol {
  key: string; label: string; group: string; width: number; ft: Ft; origin: Origin;
  measure?: Measure; perPortfolio?: boolean; extraField?: string; singleOnly?: boolean;
  text?: (r: ConsolidatedRow) => string; cell?: (r: ConsolidatedRow) => React.ReactNode;
  srcNote?: string;
}
/** A column on screen: a BaseCol for one scope (a portfolio, or the Total). */
interface Col {
  key: string; base: string; label: string; width: number; ft: Ft; origin: Origin; srcNote?: string;
  measure?: Measure; scope?: Scope; notCurrent?: string;
  /** HP-C2 two-line heading: set on a measure split by portfolio — line 1 `group` (the measure,
   *  spanning its columns), line 2 `sub` (the portfolio, or Total). */
  group?: string; sub?: string; isTotal?: boolean;
  text: (r: ConsolidatedRow) => string; value?: (r: ConsolidatedRow) => number | null;
  cell?: (r: ConsolidatedRow) => React.ReactNode;
}

// The file's other columns (HP-F): one portfolio only, hidden to start. C-411 (BNP, checked on real
// lines 2026-10-27): the currency of each P&L column in its heading, and how the bank computes it.
const FILE_EXTRA: [string, string, Ft, number, string?][] = [
  ['ref_currency', "Portfolio's Ref Ccy (file)", 'enum', 170], ['interest_rate', 'Interest rate', 'num', 110],
  ['pct_portfolio', '% portfolio (file)', 'num', 130], ['buy_date', 'Buy date', 'date', 110],
  ['purchase_currency', 'Purchase currency', 'enum', 130],
  ['capital_pnl', 'Capital P&L (Sec Ccy)', 'num', 160,
    "in the security's currency (Sec Ccy): Quantity × (Price − Average purchase price) — the gain or loss on the price alone"],
  ['currency_pnl', 'Currency P&L (Ref Ccy)', 'num', 170,
    "in the portfolio's reference currency: Quantity × Average purchase price × (today's exchange rate − Average buy exchange rate) — what the exchange rate added or took away since purchase; Total P&L = Capital P&L at today's rate + Currency P&L"],
  ['total_pnl_pct', 'Total % P&L', 'num', 110,
    "the price return without the currency effect: Capital P&L ÷ (Quantity × Average purchase price + accrued interest, both in the security's currency) × 100; the bank caps it at 9,999.00 (e.g. when it has no cost)"],
  ['subtype', 'Subtype', 'enum', 130], ['strike_price', 'Strike price', 'num', 110],
  ['avg_buy_fx', 'Average buy exchange rate', 'num', 170],
];
const FILE_EXTRA_KEYS = FILE_EXTRA.map(([k]) => k);
const DEFAULT_HIDDEN = ['category', 'accrued', 'clean_ref', 'accrued_ref', 'avg_price', 'total_cost', 'total_pnl', ...FILE_EXTRA_KEYS];
/** REF-HL (Pedro 2026-10-22: "We do not want to mistake a GBP valuation for a EUR one"): the
 *  measures whose figures depend on the reference currency. When it is not EUR, their headings,
 *  their Gross / Net / Total cells, the Reference currency picker and the summary totals are
 *  filled yellow (app-language §2: a warning, nothing blocked; pattern 20). Pedro: "only headers
 *  and Totals cells", so the group subtotal rows are not coloured. */
const REF_MEASURES = new Set<Measure>(['fx', 'value_ref', 'clean_ref', 'accrued_ref', 'total_cost', 'total_pnl']);
const REF_BG = 'var(--mantine-color-yellow-2)';
/** RATE-SHADE: a line's FX cell that uses a substituted (e.g. typed) rate — the same light
 *  colour as the rate chip (Mantine `yellow`, variant `light`). */
const SUB_BG = 'var(--mantine-color-yellow-light)';
/** COST-SRC B (0127): a cost / P&L figure from a cost entered in the app (not the custodian's). */
const ENTERED_BG = 'var(--mantine-color-cyan-light)';   // cyan: violet means "incomplete record" (app-language)
const REF_MARK: React.CSSProperties = { background: REF_BG, padding: '0 4px', borderRadius: 3 };

function baseColumns(c: ConsolidatedView, ref: string, single: string | null): BaseCol[] {
  const firstLine = (r: ConsolidatedRow) => Object.values(r.by_portfolio)[0];
  const head: BaseCol[] = [
    { key: 'name', label: 'Security', group: 'Security', width: 300, ft: 'text', origin: 'master',
      srcNote: 'the name in the Securities Master; for a line not in the master (no ISIN), the name in the file',
      text: (r) => r.name,
      cell: (r) => (
        <Group gap={6} wrap="nowrap" style={{ overflow: 'hidden' }}>
          {/* VP-LINK (Pedro 2026-10-26: "security names in View portfolios have deep link to
              Securities, with that security already filtered with the Search"): a line in the
              master opens it there (detail open, Search = its ISIN), as from a bucket. */}
          {r.security_id && !r.is_cash && !r.is_loan ? (
            <Anchor component={Link} size="sm" onClick={(e) => e.stopPropagation()}
              to={securityLink(r.isin)}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '1 1 auto', fontSize: 'inherit' }}>
              {r.name}
            </Anchor>
          ) : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: '1 1 auto' }}>{r.name}</span>}
          {/* Pedro 2026-10-23: "The non-classified pills are slightly redundant … They are classified
              as such anyway. Can we replace them for 'non-approved', which is much more relevant". */}
          {notApproved(r) && (
            <Tooltip withArrow label="Not Approved in Securities: it can be kept or sold, not bought" openDelay={300}>
              <Badge size="xs" color="yellow" variant="light" style={{ flex: '0 0 auto' }}>Non-approved</Badge>
            </Tooltip>)}
        </Group>) },
    { key: 'isin', label: 'ISIN / account', group: 'Security', width: 150, ft: 'text', origin: 'file',
      srcNote: "a security's ISIN links it to the Securities Master, with the currency; a cash or loan line shows its account code (the file's Denomination)",
      text: (r) => r.isin ?? r.account_code ?? '-' },
    // TQ1 (Pedro 2026-10-23: "Portfolios view needs 2 additional columns that carry the BB Ticker
    // and the FIGI information from Securities"; shown by default).
    { key: 'bb_ticker', label: 'BB Ticker', group: 'Security', width: 140, ft: 'text', origin: 'master',
      srcNote: "the Securities Master's BB Ticker; - for a line not in the master", text: (r) => r.bb_ticker ?? '-' },
    { key: 'figi', label: 'FIGI', group: 'Security', width: 130, ft: 'text', origin: 'master',
      srcNote: "the Securities Master's FIGI; - for a line not in the master", text: (r) => r.figi ?? '-' },
    { key: 'currency', label: 'Sec Ccy', group: 'Security', width: 90, ft: 'enum', origin: 'file',
      srcNote: "the security's (position) currency, Sec Ccy", text: (r) => r.currency },
    { key: 'category', label: 'Category (file)', group: 'Security', width: 170, ft: 'enum', origin: 'file',
      text: (r) => firstLine(r)?.category ?? '-' },
  ];
  const cls: BaseCol[] = c.lenses.flatMap((lz) => lz.tiers.map((t): BaseCol => ({
    key: `cls:${lz.code}:${t.level}`, label: t.name, group: lz.name, width: 150, ft: 'lens', origin: 'master',
    srcNote: `${lz.name}, read live: classifying a security changes every stored date`,
    text: (r) => r.classifications[lz.code]?.[t.level - 1] ?? '',
  })));
  // 3a: shown as the file gives it when every chosen portfolio's reference currency is `ref`.
  const asGiven = c.portfolios.filter((p) => p.used_date).every((p) => (p.ref_currency ?? 'EUR') === ref);
  const conv = asGiven ? '' : `, converted to ${ref} at the custodian's rate of the view's date`;
  const m = (key: string, measure_: Measure, label: string, group: string, width: number,
    perPortfolio: boolean, origin: Origin, srcNote?: string): BaseCol =>
    ({ key, measure: measure_, label, group, width, ft: 'num', perPortfolio, origin, srcNote });
  // CCY-LBL (Pedro 2026-10-23: "have the tooltips and Headers be specific in saying Sec Ccy and
  // Ref Ccy"): every amount names its currency. Sec Ccy = the security's (position) currency;
  // Ref Ccy = the reference currency the page shows (the picker), here `ref`.
  const R = `Ref Ccy ${ref}`;
  const measures: BaseCol[] = [
    m('quantity', 'quantity', 'Quantity', 'Quantity', 120, true, 'file'),
    m('price', 'price', single ? 'Price (Sec Ccy)' : 'Price (Sec Ccy, weighted avg)', 'Price (Sec Ccy)', 130, false, single ? 'file' : 'calc',
      single ? "in the security's currency (Sec Ccy); a bond's price is per 100 nominal"
        : "in the security's currency (Sec Ccy): the portfolios' own prices weighted by quantity, Σ (price × quantity) ÷ Σ quantity (a bond's price is per 100 nominal)"),
    m('value_pos', 'value_pos', 'Value (Sec Ccy)', 'Value (Sec Ccy)', 130, true, 'file',
      "in the security's currency (Sec Ccy), accrued interest included (the file's \"Valuation pos. curr.\", a dirty value)"),
    m('fx', 'fx', `FX (Sec Ccy → ${R})`, 'FX', 140, false, asGiven ? 'file' : 'calc',
      `${ref} (Ref Ccy) per 1 unit of the security's currency (Sec Ccy): the custodian's exchange rate (the file's "Exchange rate pos. curr./ref. curr."), ${asGiven ? 'as the file gives it' : `crossed to ${ref} at the custodians' rates of the view's date`}; exactly 1 when the security is in ${ref}. A rate, not Value ÷ Value`),
    m('value_eur', 'value_ref', `Value (${R})`, `Value (${R})`, 140, true, 'calc',
      `in the reference currency (${R}): the file's Net value (clean) plus its Accrued interest, both in the portfolio's reference currency (the Net value leaves the accrued interest out)${conv}`),
    m('clean_ref', 'clean_ref', `Clean value (${R})`, `Clean value (${R})`, 150, true, asGiven ? 'file' : 'calc',
      `in the reference currency (${R}): the file's Net value, without accrued interest${conv}`),
    m('accrued_ref', 'accrued_ref', `Accrued interest (${R})`, `Accrued interest (${R})`, 170, true, 'calc',
      `in the reference currency (${R}): the file's Accrued interest, which the file gives in the portfolio's reference currency${conv}; Value = Clean value + Accrued interest`),
    m('pct', 'pct', '% of portfolio', '% of portfolio', 110, true, 'calc',
      "of the GROSS value, loans left out (a portfolio's column: that portfolio's; Total: the consolidated gross)"),
    // TRADE-CONS 4b (Pedro 2026-10-30): with several portfolios, each portfolio's holding as a share
    // of ALL of them — every cell of every row and portfolio column adds up to 100%.
    ...(single ? [] : [m('pct_cons', 'pct_cons', '% of consolidation', '% of consolidation', 120, true, 'calc',
      "a portfolio's column: its holding of the line as a share of the combined GROSS value of every portfolio shown (loans left out), so all its cells, every row and every portfolio, add up to 100%; Total: the line's share of the whole (= % of portfolio's Total)")]),
    m('accrued', 'accrued', "Accrued interest (file, portfolio's Ref Ccy)", "Accrued interest (file, portfolio's Ref Ccy)", 190, false, 'file',
      "as the file gives it, in each portfolio's own reference currency (Ref Ccy), not converted to the currency shown; see Accrued interest (Ref Ccy) for that"),
    // C-411 (checked on real BNP lines 2026-10-27): Average purchase price is in Sec Ccy; Total cost
    // is in the portfolio's reference currency (Quantity × APP × Average buy exchange rate); Total
    // P&L = Net value (clean) − Total cost. No cost from the bank (0): shown as given + banner.
    m('avg_price', 'avg_price', 'Avg purchase price (Sec Ccy)', 'Avg purchase price (Sec Ccy)', 170, true, single ? 'file' : 'calc',
      single ? "the file's Average purchase price, in the security's currency (Sec Ccy); a bond's is per 100 nominal. 0 when the custodian gives no cost (the yellow banner above the table names those lines)"
        : "in the security's currency (Sec Ccy): the portfolios' own Average purchase prices weighted by quantity (a line without a cost from the custodian counts with its 0; the yellow banner names it)"),
    m('total_cost', 'total_cost', `Total cost (${R})`, `Total cost (${R})`, 150, true, asGiven ? 'file' : 'calc',
      `in the reference currency (${R}): the file's Total cost = Quantity × Average purchase price × Average buy exchange rate, which the file gives in the portfolio's reference currency${conv}. 0 when the custodian gives no cost: shown and totalled as given, and named in the yellow banner above the table (its cost and P&L are not reliable)`),
    m('total_pnl', 'total_pnl', `Total P&L (${R})`, `Total P&L (${R})`, 150, true, asGiven ? 'file' : 'calc',
      `in the reference currency (${R}): the file's Total P&L = Net value (clean, without accrued interest) − Total cost, both in the portfolio's reference currency; = Capital P&L at today's rate + Currency P&L${conv}`),
  ];
  const extra: BaseCol[] = FILE_EXTRA.map(([key, label, ft, width, srcNote]) => ({
    key, label, group: 'File (other columns)', width, ft, origin: 'file' as Origin, extraField: key, singleOnly: true, srcNote,
  }));
  return [...head, ...cls, ...measures, ...extra];
}

/** Expand the base columns into the columns on screen (Q6: by measure). With several
 *  portfolios, a measure `splitOn` shows a column per portfolio and the Total; otherwise the
 *  Total only (HP-C2: the portfolios on screen are always the ones totalled). */
function screenColumns(base: BaseCol[], c: ConsolidatedView, ctx: Ctx, single: string | null,
  splitOn: (baseKey: string) => boolean): Col[] {
  // Every portfolio with positions gets its columns, also one left out of the totals (rung 4).
  const shown = c.portfolios.filter((p) => p.used_date).map((p) => p.ref);
  const out: Col[] = [];
  const pmeta = Object.fromEntries(c.portfolios.map((p) => [p.ref, p]));
  const fmtFor = (m: Measure) => (m === 'quantity' ? qty : m === 'pct' || m === 'pct_cons' ? percent
    : m === 'price' || m === 'avg_price' ? (v: number | null) => fmt(v, 2, 6)
      : m === 'fx' ? (v: number | null) => fmt(v, 4, 8) : amount);
  for (const b of base) {
    if (!b.measure) {
      if (b.extraField) {
        if (!single) continue;
        const f = b.extraField;
        const raw = (r: ConsolidatedRow) => (r.by_portfolio[single] as unknown as Record<string, string | null> | undefined)?.[f] ?? null;
        out.push({ key: b.key, base: b.key, label: b.label, width: b.width, ft: b.ft, origin: b.origin,
          text: (r) => { const v = raw(r); return v == null || v === '' ? '-' : b.ft === 'num' ? fmt(Number(v), 0, 8) : v; },
          value: b.ft === 'num' ? (r) => { const v = raw(r); return v == null ? null : Number(v); } : undefined });
        continue;
      }
      out.push({ key: b.key, base: b.key, label: b.label, width: b.width, ft: b.ft, origin: b.origin,
        srcNote: b.srcNote, text: b.text!, cell: b.cell });
      continue;
    }
    const m = b.measure;
    const f = fmtFor(m);
    const mk = (scope: Scope, key: string, label: string, origin: Origin, notCurrent?: string): Col => ({
      key, base: b.key, label, width: b.width, ft: 'num', origin, srcNote: b.srcNote, measure: m, scope, notCurrent,
      value: (r) => measure(r, scope, m, ctx), text: (r) => f(measure(r, scope, m, ctx)),
    });
    if (single) { out.push(mk(TOTAL, b.key, b.label, b.origin)); continue; }
    if (b.perPortfolio && splitOn(b.key)) {
      for (const p of shown) {
        const meta = pmeta[p];
        const nc = meta && meta.used_date && !meta.current ? meta.used_date : undefined;
        const sub = meta ? shortLabel(meta) : splitRef(p).number.slice(-6);
        out.push({ ...mk(p, `${b.key}:${p}`, `${b.label} · ${sub}`, b.origin === 'calc' ? 'calc' : 'file', nc),
          group: b.label, sub });
      }
      out.push({ ...mk(TOTAL, `${b.key}:total`, `${b.label} · Total`, 'calc'), group: b.label, sub: 'Total', isTotal: true });
    } else {
      // Total only: one column, "(Total)" where the measure could be split.
      out.push({ ...mk(TOTAL, `${b.key}:total`, b.perPortfolio ? `${b.label} (Total)` : b.label,
        b.perPortfolio ? 'calc' : b.origin), isTotal: b.perPortfolio });
    }
  }
  return out;
}

/** TRADE-UX 2: the columns trade mode opens with (Pedro: "Security, ISIN, BB ticker, Quantity,
 *  Price, Value (Sec Ccy), Value (Ref Ccy), % of Portfolio"). */
const TRADE_VISIBLE = ['name', 'isin', 'bb_ticker', 'quantity', 'price', 'value_pos', 'value_eur', 'pct', 'pct_cons'];

/** TRADE-UX 3: text width in the grid's font (13px), measured once per string on a canvas. */
let measureCtx: CanvasRenderingContext2D | null | undefined;
let measureFont = '';
function textWidth(s: string, bold = false): number {
  if (measureCtx === undefined) {
    measureCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    measureFont = typeof document === 'undefined' ? 'sans-serif' : getComputedStyle(document.body).fontFamily;
  }
  if (!measureCtx) return s.length * 7.5;
  measureCtx.font = `${bold ? 600 : 400} 13px ${measureFont}`;
  return measureCtx.measureText(s).width;
}

/** A column's width: its widest cell (a total may be a few digits wider) or the longest word of
 *  its heading (headings wrap) plus the sort / origin icons, whichever is wider. */
function fitWidth(col: Col, rows: ConsolidatedRow[]): number {
  let cell = 0;
  for (const r of rows) cell = Math.max(cell, textWidth(col.text(r)));
  if (col.ft === 'num') cell += 16;                                    // totals run wider
  if (col.base === 'name') {
    cell += 34;                                                        // group indent
    if (rows.some(notApproved)) cell += 96;                           // the "Non-approved" badge
  }
  const label = col.sub ?? col.label;
  const word = Math.max(...label.split(/\s+/).map((w) => textWidth(w, true)));
  return Math.ceil(Math.min(Math.max(cell + 22, word + 44, 56), 420));
}

/** The table fills the window down to its bottom edge (HP-V). */
function useFillHeight(ref: React.RefObject<HTMLDivElement | null>, min = 240): number {
  const [h, setH] = useState(480);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const next = Math.max(min, Math.floor(window.innerHeight - top - 20));
    if (top > 0 && next !== h) setH(next);
  });
  useEffect(() => {
    const onResize = () => {
      const el = ref.current;
      if (el) setH(Math.max(min, Math.floor(window.innerHeight - el.getBoundingClientRect().top - 20)));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ref, min]);
  return h;
}

const ROW_ACCESS = CONSOLIDATED_ACCESS;
const rowsOf = (node: GroupNode<ConsolidatedRow>): ConsolidatedRow[] =>
  node.children.length ? node.children.flatMap(rowsOf) : node.lines;
const INDENT = 16;

type GridRow =
  | { kind: 'group'; node: GroupNode<ConsolidatedRow> }
  | { kind: 'line'; row: ConsolidatedRow; depth: number; i: number };

// ── the grid ─────────────────────────────────────────────────────────────────

/** SHADE-TIPS (Pedro 2026-10-22: "tooltips on the shaded cells that explain why they are
 *  shaded, each for its own reason"): the REF-HL reason. */
const refWhy = (ccy: string) => `These figures are in ${ccy}, not EUR.`;

function ConsolidatedGrid({ c, views, picker, tradable, preferredRef, consolidation, openDraft, openRun, onOpenRun, onDate, onView, onSubstitute, onShow, onRateChoice }: {
  c: ConsolidatedView; views: ClassificationScheme[]; picker: React.ReactNode; tradable?: boolean;
  preferredRef?: string;
  /** TRADE-CONS 4b: the one consolidation shown, if that is all that is shown: traded as one. */
  consolidation?: Consolidation | null;
  openDraft?: { id: string; ref: string; n: number } | null;
  openRun?: { id: string; n: number } | null;
  /** Show a run's consolidation and open the run in its trade mode. */
  onOpenRun: (runId: string) => void;
  onDate: (d: string) => void; onView: (id: string | null) => void;
  onSubstitute: (number: string, date: string | null) => void;
  /** TOT-DASH: the currency shown (the server reports a missing rate for it too). */
  onShow: (ccy: string) => void;
  /** TOT-DASH: a fallback rate for "bank:CCY" ('last' | 'from:<bank>' | a typed rate), or null to drop it. */
  onRateChoice: (key: string, choice: string | null) => void;
}) {
  const [hidden, setHidden] = useState<Set<string>>(() => {
    const h = new Set(readLocal<string[]>('portfolios.hiddenCols', DEFAULT_HIDDEN));
    if (!readLocal<boolean>('portfolios.hpfColsHidden', false)) {
      FILE_EXTRA_KEYS.forEach((k) => h.add(k));
      try { localStorage.setItem('portfolios.hpfColsHidden', 'true'); } catch { /* private window */ }
    }
    // ACC-1's two new columns start hidden also for someone whose column choice was saved before.
    if (!readLocal<boolean>('portfolios.acc1ColsHidden', false)) {
      ['clean_ref', 'accrued_ref'].forEach((k) => h.add(k));
      try { localStorage.setItem('portfolios.acc1ColsHidden', 'true'); } catch { /* private window */ }
    }
    return h;
  });
  useEffect(() => { try { localStorage.setItem('portfolios.hiddenCols', JSON.stringify([...hidden])); } catch { /* private window */ } }, [hidden]);
  const [freezeKey, setFreezeKey] = useState<string>(() => readLocal<string>('portfolios.freezeKey', 'name'));
  useEffect(() => { try { localStorage.setItem('portfolios.freezeKey', JSON.stringify(freezeKey)); } catch { /* private window */ } }, [freezeKey]);
  const [levelKeys, setLevelKeys] = useState<string[]>(() =>
    readLocal<string[] | null>('portfolios.groupLevels', null)
    ?? (c.lenses[0] ? [levelKey({ code: c.lenses[0].code, tier: 1 })] : []));
  useEffect(() => { try { localStorage.setItem('portfolios.groupLevels', JSON.stringify(levelKeys)); } catch { /* private window */ } }, [levelKeys]);
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [ref, setRef] = useState(preferredRef ?? 'EUR');
  const inView = c.portfolios.filter((p) => p.used_date).map((p) => p.ref);
  // 3a: the currencies offered: every currency with a rate that day, plus the chosen
  // portfolios' own reference currencies (shown in its own currency, a portfolio needs no
  // rate: Pedro's smoke 2026-10-21, a GBP portfolio on a day without a GBP rate).
  const ownCcys = [...new Set(c.portfolios.filter((p) => p.used_date).map((p) => p.ref_currency ?? 'EUR'))];
  const refOptions = [...new Set([...Object.keys(c.rates), ...ownCcys])].sort();
  // MR-C (0126): on an agreement's Portfolios tab, its Preferred Ref Ccy is the preset whenever
  // it is offered that day; otherwise the rules below, as before.
  const preset = preferredRef && refOptions.includes(preferredRef) ? preferredRef : null;
  // A currency no longer offered on the new date or choice: back to the preset, else EUR.
  useEffect(() => { if (!refOptions.includes(ref)) setRef(preset ?? 'EUR'); }, [refOptions.join(','), ref]); // eslint-disable-line react-hooks/exhaustive-deps
  // A new choice of portfolios opens in the preset, else in their own reference currency when
  // they share one (EUR for every BNP file so far); the picker changes it as before.
  const ownRef = ownCcys.length === 1 ? ownCcys[0] : null;
  useEffect(() => { if (preset ?? ownRef) setRef((preset ?? ownRef) as string); }, [inView.join(','), ownRef, preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const single = inView.length === 1 ? inView[0] : null;
  // HP-C2 (Pedro 2026-10-20): the portfolios on screen are the ones totalled (Portfolios is
  // the one set). Which measures get a column per portfolio: the switch (Totals only / Chosen
  // measures / All measures) and, for "Chosen", the "per portfolio" ticks in Columns.
  const [splitMode, setSplitMode] = useState<'totals' | 'chosen' | 'all'>(
    () => readLocal<'totals' | 'chosen' | 'all'>('portfolios.splitMode', 'chosen'));
  useEffect(() => { try { localStorage.setItem('portfolios.splitMode', JSON.stringify(splitMode)); } catch { /* private window */ } }, [splitMode]);
  // PF-MAX: above 20 portfolios only the consolidated Total columns (the user's choice is kept
  // and comes back when fewer portfolios are shown).
  const manyPortfolios = inView.length > TOTALS_ONLY_ABOVE;
  const effSplit: 'totals' | 'chosen' | 'all' = manyPortfolios ? 'totals' : splitMode;
  const [splitSet, setSplitSet] = useState<Set<string>>(
    () => {
      const s = new Set(readLocal<string[]>('portfolios.splitMeasures', ['quantity', 'value_eur', 'pct', 'pct_cons']));
      // TRADE-CONS 4b: % of consolidation only means something per portfolio — ticked "per portfolio"
      // once, also for a choice saved before it existed (untick it in Columns to see the Total only).
      if (!readLocal<boolean>('portfolios.pctConsSplit', false)) {
        s.add('pct_cons');
        try { localStorage.setItem('portfolios.pctConsSplit', 'true'); } catch { /* private window */ }
      }
      return s;
    });
  useEffect(() => { try { localStorage.setItem('portfolios.splitMeasures', JSON.stringify([...splitSet])); } catch { /* private window */ } }, [splitSet]);
  const splitOn = (k: string) => effSplit === 'all' || (effSplit === 'chosen' && splitSet.has(k));

  // HP-L: every % is a share of the GROSS value (loans left out); a server without the
  // gross figure falls back to the net total.
  // TOT-DASH ladder rung 4 (Pedro 2026-10-22): a portfolio that cannot be had in the shown
  // currency (no rate, none chosen or typed) is LEFT OUT of the totals and every %, and named
  // in the notice above the table; its own lines still show. When none can be had, none is
  // left out (the figures then read "-" as before).
  const ctx: Ctx = useMemo(() => {
    const full: Ctx = {
      inView,
      portfolioTotalEur: Object.fromEntries(c.portfolios.filter((p) => p.total_value_eur != null)
        .map((p) => [p.ref, Number(p.gross_value_eur ?? p.total_value_eur)])),
      rates: Object.fromEntries(Object.entries(c.rates).map(([k, v]) => [k, Number(v)])),
      ratesByBank: Object.fromEntries(Object.entries(c.rates_by_bank ?? {}).map(([b, rs]) => [b,
        Object.fromEntries(Object.entries(rs).map(([k, v]) => [k, Number(v)]))])),
      substituted: (c.rate_sources ?? []).reduce<Record<string, string[]>>((acc, s) => {
        (acc[s.bank] ??= []).push(s.currency);
        return acc;
      }, {}),
      ref,
      portfolioRef: Object.fromEntries(c.portfolios.filter((p) => p.used_date).map((p) => [p.ref, {
        ccy: p.ref_currency ?? 'EUR',
        gross: p.gross_value_ref != null ? Number(p.gross_value_ref) : null,
      }])),
    };
    const convertible = inView.filter((p) => grossIn(p, full) != null);
    return convertible.length > 0 && convertible.length < inView.length ? { ...full, inView: convertible } : full;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c, ref, inView.join(',')]);
  const leftOut = inView.filter((p) => !ctx.inView.includes(p));
  // The page tells the server which currency it shows, so a missing rate for it is reported too.
  useEffect(() => { onShow(ref); }, [ref]); // eslint-disable-line react-hooks/exhaustive-deps

  // TRADE-1: trade mode, on View portfolio when `tradable` (TradeMode.tsx). Its columns follow the
  // portfolio's own; its Buy / Sell / Net total rows follow the Total.
  const singleTrade = useTradeMode({ c, ctx, single, refCcy: ref, rows: c.rows, enabled: !!tradable && !consolidation,
    onLatest: () => onDate(c.dates[0]),
    // TP-PDF: the document's Before and after follows the grouping on screen at export.
    groupBy: { levels: validLevels(levelKeys, c.lenses), lenses: c.lenses },
    openDraft, onOpenRun });
  // TRADE-CONS 4b (S4-Q1): one consolidation shown on its own is traded as one (a run).
  const runTrade = useConsolidationTradeMode({ c, ctx, refCcy: ref, rows: c.rows, enabled: !!tradable && !!consolidation,
    onLatest: () => onDate(c.dates[0]), consolidation: consolidation ?? null, openRun,
    groupBy: { levels: validLevels(levelKeys, c.lenses), lenses: c.lenses } });
  const trade = consolidation ? runTrade : singleTrade;

  const base = useMemo(() => baseColumns(c, ref, single), [c, ref, single]);
  // TRADE-UX 2 (Pedro 2026-10-23: "The table enters Trade mode with only the following columns
  // visible"): trade mode has its own column choice, remembered apart from View portfolio's.
  const [tradeHiddenStored, setTradeHiddenStored] = useState<string[] | null>(() => readLocal<string[] | null>('trades.hiddenCols', null));
  useEffect(() => {
    try { if (tradeHiddenStored) localStorage.setItem('trades.hiddenCols', JSON.stringify(tradeHiddenStored)); } catch { /* private window */ }
  }, [tradeHiddenStored]);
  const tradeHidden = useMemo(() => new Set(tradeHiddenStored ?? base.map((b) => b.key).filter((k) => !TRADE_VISIBLE.includes(k))),
    [tradeHiddenStored, base]);
  const effHidden = trade.active ? tradeHidden : hidden;
  const levels = validLevels(levelKeys, c.lenses);
  const colOrder = useColumnOrder('portfolios.columnOrder', base.map((b) => b.key));
  const orderedBase = ordered(base, colOrder.order);
  const groupedKeys = new Set(levels.map((l) => `cls:${l.code}:${l.tier}`));
  const unavailable: Record<string, string> = Object.fromEntries([...groupedKeys].map((k) => [k, 'used for grouping']));
  if (!single) base.filter((b) => b.singleOnly).forEach((b) => { unavailable[b.key] = 'one portfolio only'; });
  const colWidths = useColumnWidths('portfolios.columnWidths');
  const cols = useMemo(
    () => screenColumns(orderedBase.filter((b) => !effHidden.has(b.key) && !groupedKeys.has(b.key)), c, ctx, single,
      splitOn),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedBase.map((b) => b.key).join('|'), [...effHidden].join('|'), levelKeys.join('|'), c, ctx, single,
      effSplit, [...splitSet].join('|')]);
  // TRADE-UX 3 (Pedro 2026-10-23: "Make all columns as narrow as possible without loss of
  // in-cell content … use two lines for the headers"): each column starts at the width of its
  // widest cell (headings wrap); a width dragged by the user still wins.
  const fitted = useMemo(() => {
    const out: Record<string, number> = {};
    for (const col of cols) out[col.key] = fitWidth(col, c.rows);
    return out;
  }, [cols, c.rows]);
  const visible = cols.map((col) => ({ ...col, width: colWidths.widthOf(col.key, fitted[col.key] ?? col.width) }));
  const drag = useHeadingDrag(colOrder.place);
  const lefts = visible.reduce<number[]>((acc, _x, i) => { acc[i] = i === 0 ? 0 : acc[i - 1] + visible[i - 1].width; return acc; }, []);
  const totalW = visible.reduce((s, x) => s + x.width, 0);
  const freezeIdx = visible.findIndex((x) => x.key === freezeKey);
  const toggleCol = (k: string) => {
    if (trade.active) {
      const n = new Set(tradeHidden); if (n.has(k)) n.delete(k); else n.add(k);
      setTradeHiddenStored([...n]);
      return;
    }
    setHidden((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  };

  const allRows = c.rows;
  // TRADE-SORT (Pedro 2026-10-24: "is there anyway for the column filters to apply to the new
  // securities as well?"): the securities added to buy feed the filter lists too, so a value only
  // a new buy has (e.g. its currency) can be chosen.
  const addedRows = trade.addedRows;
  const options = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const x of cols) {
      if (x.ft !== 'enum' && x.ft !== 'lens') continue;
      out[x.key] = [...new Set([...allRows, ...addedRows].map((r) => x.text(r)).filter((t) => t && t !== '-'))].sort();
    }
    return out;
  }, [cols, allRows, addedRows]);

  const passes = useMemo(() => {
    const active = cols.filter((x) => filterActive(x, filters[x.key]));
    return (r: ConsolidatedRow) =>
      active.every((x) => passesFilter(x.ft, filters[x.key], x.text(r), x.value ? x.value(r) : null));
  }, [cols, filters]);
  // The same filters hide or show the added rows; they stay on top, unsorted. A hidden one still
  // counts in the trade totals and the draft (a filter is a way of looking, like Traded only).
  const addedShown = useMemo(() => addedRows.filter(passes), [addedRows, passes]);
  const addedHidden = addedRows.length - addedShown.length;

  const shown = useMemo(() => {
    const out = allRows.filter(passes);
    const sc = sort && cols.find((x) => x.key === sort.key);
    if (sc) {
      const val = (r: ConsolidatedRow): number | string => (sc.value ? (sc.value(r) ?? -Infinity) : sc.text(r));
      out.sort((a, b) => {
        const x = val(a), y = val(b);
        const d = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
        return sort.dir === 'desc' ? -d : d;
      });
    }
    return out;
  }, [allRows, cols, passes, sort]);
  const activeFilters = cols.filter((x) => filterActive(x, filters[x.key])).length;

  // I1 (TRADE-1): "Traded only" hides the rows without a trade; the totals still cover the
  // portfolio (it is a way of looking at the trades, not a filter of the portfolio).
  const listed = trade.onlyTraded ? shown.filter(trade.isTraded) : shown;
  // TRADE-CONS 4b (S4-Q2): a security row of a consolidation traded as one opens into one row per
  // portfolio — its label in the first column, the portfolio's part in the trade columns.
  const subRowsOf = (row: ConsolidatedRow, depth: number): React.ReactNode[] => (trade.subRows?.(row) ?? []).map((s) => {
    const bg = s.shade ?? 'var(--mantine-color-gray-0)';
    return (
      <tr key={`sub:${s.key}`}>
        {visible.map((x, vi) => (
          <td key={x.key} style={cellStyle(vi, bg, false)}>
            {vi === 0 ? <div style={{ paddingLeft: depth * INDENT + 28, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div> : ''}
          </td>
        ))}
        {s.cells(bg)}
      </tr>
    );
  });
  const { groups, ungrouped } = useMemo(() => groupRows(listed, levels, c.lenses, ROW_ACCESS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listed, levelKeys.join('|'), c.lenses]);
  const gridRows: GridRow[] = [];
  const walk = (nodes: GroupNode<ConsolidatedRow>[]) => {
    for (const node of nodes) {
      gridRows.push({ kind: 'group', node });
      if (folded.has(node.id)) continue;
      if (node.children.length) walk(node.children);
      else node.lines.forEach((row, i) => gridRows.push({ kind: 'line', row, depth: node.depth + 1, i }));
    }
  };
  walk(groups);
  ungrouped.forEach((row, i) => gridRows.push({ kind: 'line', row, depth: 0, i }));
  const toggleFold = (id: string) => setFolded((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /** A group / total cell: the sum of the rows held in that scope, for the summable measures
   *  (value, cost, P&L, %); "-" if one of them cannot be converted; blank otherwise. */
  // HP-L smoke (Pedro 2026-10-20): a cash or loan line has no cost or P&L in the file, which
  // is "not applicable", not "unknown": it adds nothing instead of turning the whole Gross /
  // Net / group cell into "-". A SECURITY whose figure cannot be converted still gives "-".
  // REF-HL: a column whose figures are in a reference currency other than EUR.
  const nonEur = ref !== 'EUR';
  const inRef = (x: Col) => nonEur && !!x.measure && REF_MEASURES.has(x.measure);
  // RATE-SHADE (Pedro 2026-10-22, after his smoke: "keep cells shaded for this reason to the FX
  // cell itself, and no other ... the same colour ... as the bubble warning"): only a line's FX
  // cell that uses a substituted rate is shaded, in the chip's light yellow.
  const subUsed = (x: Col, rows: ConsolidatedRow[]) =>
    x.measure === 'fx' && !!x.scope && rows.some((r) => usesSubstitute(r, x.scope!, 'fx', ctx));
  // Pedro 2026-10-22: the total rows also name the shown currency when it is not EUR, in the
  // same yellow as the other non-EUR marks ("the same shade of yellow as elsewhere with same
  // meaning"): the summary line's yellow mark (REF_MARK), dark text on it to stay readable.
  const refTag = nonEur
    ? <Text span fw={700} ml={6} title={refWhy(ref)} style={REF_MARK}>{ref}</Text>
    : null;
  // SHADE-TIPS: the FX cell's own reason — which substituted rate(s) it uses.
  const subWhy = (x: Col, row: ConsolidatedRow): string | undefined => {
    if (!subUsed(x, [row])) return undefined;
    const scopes = x.scope === TOTAL ? ctx.inView : [x.scope!];
    const srcs = (c.rate_sources ?? []).filter((s) =>
      (s.currency === row.currency || s.currency === ref)
      && scopes.some((p) => !!row.by_portfolio[p] && p.startsWith(`${s.bank}:`)));
    return srcs.map((s) => sourceWhy(s)).join(' ');
  };
  // COST-SRC B (0127): a cost / P&L cell whose figure comes from a cost entered in the app is
  // shaded cyan and says so, with the evidence noted when it was entered.
  // COST-SRC-P&L (Pedro 2026-10-27: "go with (a)"): the file's Capital P&L, Currency P&L and
  // Total % P&L are recomputed from an entered cost too (one-portfolio columns), so cyan as well.
  const enteredWhy = (x: Col, row: ConsolidatedRow): string | undefined => {
    const fileCol = ['capital_pnl', 'currency_pnl', 'total_pnl_pct'].includes(x.base);
    if (!fileCol && (!x.measure || !['avg_price', 'total_cost', 'total_pnl'].includes(x.measure) || !x.scope)) return undefined;
    const scopes = fileCol || x.scope === TOTAL ? ctx.inView : [x.scope!];
    const notes = scopes.map((p) => row.by_portfolio[p]).filter((l) => l?.cost_entered).map((l) => l!.cost_note ?? '');
    return notes.length ? `Cost entered in the app (the custodian gives none): ${notes.join(' · ')}` : undefined;
  };
  const summary = (x: Col, rows: ConsolidatedRow[]): string => {
    if (!x.measure || ![...SUMMABLE, 'pct' as Measure, 'pct_cons' as Measure].includes(x.measure)) return '';
    if (x.measure === 'pct' || x.measure === 'pct_cons') {
      // PREC-1 C1 (Pedro 2026-10-22): a group / total % is computed from the VALUES (Σ value ÷
      // gross), never by adding percentages, so it is exact (the Gross total reads 100.00%).
      // % of consolidation: of every portfolio's gross, whatever the column.
      const scopeNums = x.scope === TOTAL ? ctx.inView : [x.scope!];
      const held = rows.filter((r) => scopeNums.some((p) => r.by_portfolio[p]));
      if (held.length === 0) return '';
      const vals = held.map((r) => measure(r, x.scope!, 'value_ref', ctx));
      const grosses = (x.measure === 'pct_cons' ? ctx.inView : scopeNums).map((p) => grossIn(p, ctx));
      if (vals.every((v) => v != null) && grosses.every((g) => g != null)) {
        const g = (grosses as number[]).reduce((a, b) => a + b, 0);
        return g === 0 ? '-' : percent(((vals as number[]).reduce((a, b) => a + b, 0) / g) * 100);
      }
      // No rate for the shown currency: fall back to adding the (unrounded) per-line shares,
      // which the server gives in each portfolio's own currency.
    }
    let s = 0, counted = 0;
    for (const r of rows) {
      const scopeNums = x.scope === TOTAL ? ctx.inView : [x.scope!];
      if (!scopeNums.some((p) => r.by_portfolio[p])) continue;   // not held: adds nothing
      const v = measure(r, x.scope!, x.measure, ctx);
      if (v == null) {
        if ((r.is_cash || r.is_loan) && (x.measure === 'total_cost' || x.measure === 'total_pnl')) continue;
        return '-';
      }
      s += v;
      counted++;
    }
    if (counted === 0) return '';
    return x.measure === 'pct' ? percent(s) : amount(s);
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const height = useFillHeight(tableRef);
  // The Gross total row sticks just above the Net total row (HP-L): measure the latter.
  const netRowRef = useRef<HTMLTableRowElement>(null);
  const [netRowH, setNetRowH] = useState(29);
  useLayoutEffect(() => {
    const h = netRowRef.current?.getBoundingClientRect().height;
    if (h && Math.abs(h - netRowH) > 0.5) setNetRowH(h);
  });
  // HP-C2/C3: the Total of a split measure has a bold heading and figures; a heavier line
  // bounds each split measure's group of columns (Pedro 2026-10-20: "separating that column
  // group … from the next"), on its right edge and just before it.
  // Pedro 2026-10-20: a lighter line, and no darker fill on the Total or the measure row (the bold is enough).
  const GROUP_EDGE = '2px solid var(--mantine-color-gray-4)';
  const isGroupEdge = (vi: number) => {
    const x = visible[vi], next = visible[vi + 1];
    const lastOfGroup = !!x.group && (!next || next.group !== x.group || next.base !== x.base);
    const beforeGroup = !!next?.group && (next.group !== x.group || next.base !== x.base);
    return lastOfGroup || beforeGroup;
  };
  const cellStyle = (vi: number, bg: string, isHead: boolean, bold = false, top = 0): React.CSSProperties => {
    const x = visible[vi];
    const frozen = vi <= freezeIdx;
    return {
      width: x.width, minWidth: x.width, maxWidth: x.width, padding: '4px 10px', boxSizing: 'border-box',
      background: bg, borderBottom: '1px solid var(--mantine-color-gray-3)',
      borderRight: isGroupEdge(vi) ? GROUP_EDGE
        : vi === freezeIdx ? '2px solid var(--mantine-color-gray-4)' : '1px solid var(--mantine-color-gray-2)',
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13,
      fontWeight: bold || (x.isTotal && x.group) ? 600 : undefined, textAlign: !isHead && x.ft === 'num' ? 'right' : 'left',
      position: (isHead || frozen) ? 'sticky' : undefined, top: isHead ? top : undefined,
      left: frozen ? lefts[vi] : undefined, zIndex: isHead && frozen ? 4 : isHead ? 3 : frozen ? 2 : undefined,
    };
  };
  // Two heading lines when a measure is split by portfolio (Pedro 2026-10-20): line 1 the
  // measure, spanning its columns; line 2 each portfolio, then Total.
  const twoLines = visible.some((x) => !!x.group);
  const HEAD1 = 29;
  // TRADE-UX 3: headings wrap, so the first heading row may be taller than HEAD1; the second
  // row sticks just under its measured height.
  const head1Ref = useRef<HTMLTableRowElement>(null);
  const [head1H, setHead1H] = useState(HEAD1);
  useLayoutEffect(() => {
    const h = head1Ref.current?.getBoundingClientRect().height;
    if (h && Math.abs(h - head1H) > 0.5) setHead1H(h);
  });
  /** A column's own heading (sort / filter / resize). On line 2 of a split measure it reads
   *  the portfolio (or Total); elsewhere the column's full label. */
  const headCell = (x: Col, vi: number, rowSpan: number, top: number) => {
    const active = filterActive(x, filters[x.key]) || sort?.key === x.key;
    const S = ORIGINS[x.origin];
    const tip = `${x.label}. ${S.text}${x.srcNote ? `: ${x.srcNote}` : ''}.`
      + (x.notCurrent ? ` NOT CURRENT: positions of ${x.notCurrent}, not ${c.as_of}.` : '')
      + (inRef(x) ? ` ${refWhy(ref)}` : '')
      + ' Click to sort or filter; drag to move the column.';
    const bg = inRef(x) ? REF_BG
      : active ? 'var(--mantine-color-azul-1)' : x.notCurrent ? 'var(--mantine-color-yellow-2)'
      : 'var(--mantine-color-gray-1)';
    const shownLabel = x.group ? (x.sub === 'Total' ? 'Total' : `${x.sub}${x.notCurrent ? ` (${x.notCurrent})` : ''}`) : x.label;
    return (
      <th key={x.key} rowSpan={rowSpan} {...drag.props(x.base)}
        style={{ ...cellStyle(vi, bg, true, false, top), whiteSpace: 'normal', verticalAlign: 'bottom', ...drag.marker(x.base) }}>
        <Popover width={250} position="bottom-end" withArrow shadow="md" trapFocus keepMounted={false}
          opened={openCol === x.key} onChange={(o) => setOpenCol(o ? x.key : null)}>
          <Popover.Target>
            <Group gap={4} wrap="nowrap" justify="space-between" role="button"
              aria-label={`Sort or filter ${x.label}`} style={{ cursor: 'pointer' }}
              onClick={() => setOpenCol(openCol === x.key ? null : x.key)}>
              <Tooltip label={tip} multiline w={300} withArrow openDelay={300}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                  {x.notCurrent
                    ? <IconClock size={13} style={{ flex: '0 0 auto', color: 'var(--mantine-color-yellow-8)' }} />
                    : <S.icon size={13} style={{ flex: '0 0 auto', color: ORIGIN_COLOR }} aria-label={S.text} />}
                  {/* TRADE-UX 3: the heading wraps to the column's width. */}
                  <span style={{ fontWeight: x.isTotal ? 700 : 500, whiteSpace: 'normal', lineHeight: 1.2 }}>{shownLabel}</span>
                </span>
              </Tooltip>
              <span style={{ color: active ? 'var(--mantine-color-azul-7)' : 'var(--mantine-color-gray-5)', display: 'inline-flex', flex: '0 0 auto' }}>
                {sort?.key === x.key
                  ? (sort.dir === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />)
                  : <IconFilter size={14} />}
              </span>
            </Group>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnMenu col={x} options={options[x.key] ?? []} initialFilter={filters[x.key]} sort={sort}
              onSort={(dir) => { setSort((p) => (p && p.key === x.key && p.dir === dir ? null : { key: x.key, dir })); setOpenCol(null); }}
              onApply={(draft) => { setFilters((p) => ({ ...p, [x.key]: draft })); setOpenCol(null); }}
              onClear={() => { setFilters((p) => { const n = { ...p }; delete n[x.key]; return n; }); setSort((p) => (p?.key === x.key ? null : p)); setOpenCol(null); }} />
          </Popover.Dropdown>
        </Popover>
        <ColumnResizeHandle width={x.width} onResize={(w) => colWidths.setWidth(x.key, w)} />
      </th>
    );
  };

  const viewOptions = views.map((s) => ({ value: s.id, label: s.is_active ? `${s.name} (active)` : s.name }));
  const groupIds = allGroupIds(groups);
  const missing = c.portfolios.filter((p) => !p.used_date);
  const stale = c.portfolios.filter((p) => p.used_date && !p.current);
  // HP-L: gross (loans left out; ctx holds it) and net (every line) for the summary line.
  const pmetaByNo = Object.fromEntries(c.portfolios.map((p) => [p.ref, p]));
  // 3a: each portfolio in the shown currency — as the file gives it when that is its own
  // reference currency, else from EUR; one that cannot be had makes the sum "-", never partial.
  const sumOrNull = (vals: (number | null | undefined)[]) =>
    vals.some((v) => v == null || Number.isNaN(v)) ? null : (vals as number[]).reduce((s, v) => s + v, 0);
  const netIn = (p: string) => {
    const m = pmetaByNo[p];
    if (!m) return null;
    if ((m.ref_currency ?? 'EUR') === ref && m.total_value_ref != null) return Number(m.total_value_ref);
    return m.total_value_eur == null ? null : convert(Number(m.total_value_eur), 'EUR', ref, ratesFor(p, ctx));
  };
  // The totals cover the portfolios in the Total (ctx.inView: rung 4 leaves out the unconvertible).
  const grossRef = sumOrNull(ctx.inView.map((p) => grossIn(p, ctx)));
  const netRef = sumOrNull(ctx.inView.map(netIn));
  const hasLoans = allRows.some((r) => r.is_loan);
  const positions = allRows.filter((r) => !r.is_cash && !r.is_loan).length;
  const nonApproved = allRows.filter(notApproved).length;
  // COST-FIX (Pedro 2026-10-27): "show cost as 0 when the custodian has that and keep the Total P&L
  // but present a warning banner highlighting which (or at least how many) securities do not have
  // reliable cost and P&L information with a suggestion to check the No Cost tab".
  const noCost = allRows.filter((r) => !r.is_cash && !r.is_loan
    && inView.some((p) => r.by_portfolio[p] && costUnknown(r.by_portfolio[p])));
  // COST-SRC B smoke (Pedro 2026-10-27): "you can keep a banner, but now cyan, saying that the cost
  // was user input in the app".
  const entered = allRows.filter((r) => !r.is_cash && !r.is_loan
    && inView.some((p) => r.by_portfolio[p]?.cost_entered));
  const nonText = 'Not Approved in Securities: they can be kept or sold, never bought (each carries a Non-approved pill). Click to open Securities with only the not-approved ones.';

  return (
    <Stack gap={6}>
      <Group gap="xs" align="flex-end" wrap="wrap">
        {picker}
        <Select size="xs" label="Positions date" w={130} allowDeselect={false}
          data={c.dates} value={c.as_of} onChange={(d) => d && onDate(d)} />
        <Tooltip withArrow multiline w={300}
          label={`${nonEur ? `${refWhy(ref)} ` : ''}The currency the amounts are shown in. Only currencies held in some portfolio on this date are offered: the custodian's own rates for that date convert them.`}>
          <Select size="xs" label="Reference currency" w={110} allowDeselect={false}
            data={refOptions} value={ref} onChange={(v) => v && setRef(v)}
            styles={nonEur ? { input: { background: REF_BG, borderColor: 'var(--mantine-color-yellow-7)', fontWeight: 600 } } : undefined} />
        </Tooltip>
        {!single && inView.length > 1 && (
          <Tooltip withArrow multiline w={320}
            label={manyPortfolios
              ? `More than ${TOTALS_ONLY_ABOVE} portfolios: the table shows the consolidated Total columns only. Choose ${TOTALS_ONLY_ABOVE} or fewer to see a column per portfolio.`
              : "Which measures get a column for each portfolio (beside their Total): none, the ones ticked 'per portfolio' in Columns, or all. The portfolios on screen are always the ones in the Total."}>
            <Stack gap={2}>
              <Text size="xs" fw={500}>Portfolio columns</Text>
              <SegmentedControl size="xs" value={effSplit} disabled={manyPortfolios}
                onChange={(v) => setSplitMode(v as 'totals' | 'chosen' | 'all')}
                data={[{ value: 'totals', label: 'Totals only' }, { value: 'chosen', label: 'Chosen measures' },
                  { value: 'all', label: 'All measures' }]} />
            </Stack>
          </Tooltip>
        )}
        <Select size="xs" label="Classification view" w={190} allowDeselect={false}
          data={viewOptions} value={c.view?.id ?? null} onChange={(id) => { onView(id); setFolded(new Set()); }}
          disabled={viewOptions.length === 0} />
        <MultiSelect size="xs" label="Group by (in order)" w={300} clearable
          placeholder={levels.length ? undefined : 'No grouping'}
          data={levelOptions(c.lenses)} value={levels.map(levelKey)}
          onChange={(ks) => { setLevelKeys(ks); setFolded(new Set()); }} />
        <Select size="xs" label="Freeze columns through" w={170} allowDeselect={false}
          data={[{ value: '__none__', label: 'None' }, ...visible.map((x) => ({ value: x.key, label: x.label }))]}
          value={freezeIdx >= 0 ? freezeKey : '__none__'} onChange={(k) => setFreezeKey(k ?? '__none__')} />
        <ColumnsPicker size="xs" columns={orderedBase.map((b) => ({ key: b.key, label: b.label, group: b.group, origin: b.origin }))}
          hidden={effHidden} onToggle={toggleCol} onShowAll={() => (trade.active ? setTradeHiddenStored([]) : setHidden(new Set()))}
          onPlace={colOrder.place} onResetOrder={() => { colOrder.reset(); colWidths.reset(); }}
          customOrder={colOrder.custom || colWidths.custom} unavailable={unavailable}
          split={single ? undefined : {
            allowed: new Set(base.filter((b) => b.perPortfolio).map((b) => b.key)),
            on: effSplit === 'all' ? new Set(base.filter((b) => b.perPortfolio).map((b) => b.key))
              : effSplit === 'totals' ? new Set() : splitSet,
            onToggle: (k) => setSplitSet((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; }),
            disabledReason: manyPortfolios
              ? `More than ${TOTALS_ONLY_ABOVE} portfolios: Total columns only.`
              : splitMode === 'chosen' ? undefined
                : `"Portfolio columns" is on ${splitMode === 'all' ? 'All measures' : 'Totals only'}: choose "Chosen measures" to use these ticks.`,
          }} />
        {groupIds.length > 0 && (
          <Button size="xs" variant="subtle" onClick={() => setFolded(folded.size ? new Set() : new Set(groupIds))}>
            {folded.size ? 'Unfold all' : 'Fold all'}
          </Button>
        )}
        {/* TRADE-1 (Pedro: "a new button in line with Portfolios, Positions Date, etc, justified
            to the right of the page"). */}
        {trade.enabled && <div style={{ marginLeft: 'auto' }}>{trade.button}</div>}
      </Group>
      {trade.panel}

      {/* Q2: a portfolio without positions on the date is flagged loudly, never silently older. */}
      {missing.map((p) => (
        <Alert key={p.ref} color="red" py={6} icon={<IconAlertTriangle size={16} />}>
          <Group gap="sm" wrap="wrap">
            <Text size="sm">
              Portfolio <b>{p.label}</b> has no positions on <b>{c.as_of}</b>: it is left out of the view and the Total.
              {!p.latest_before && ' It has no earlier positions either.'}
            </Text>
            {p.latest_before && (
              <Button size="compact-xs" variant="light" color="red" onClick={() => onSubstitute(p.ref, p.latest_before)}>
                Use latest known positions ({p.latest_before})
              </Button>
            )}
          </Group>
        </Alert>
      ))}

      <RateNotices missing={c.missing_rates ?? []} sources={c.rate_sources ?? []} onChoose={onRateChoice}
        leftOut={leftOut.map((p) => pmetaByNo[p]?.label ?? splitRef(p).number)} shown={ref} />

      {/* COST-FIX (C-411): yellow, a warning, nothing blocked (app-language §2). */}
      {noCost.length > 0 && (
        <Alert color="yellow" py={6} icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            <b>{noCost.length} securit{noCost.length === 1 ? 'y has' : 'ies have'} no cost from the custodian</b> (Average
            purchase price and Total cost 0), so {noCost.length === 1 ? 'its' : 'their'} cost and P&amp;L are not
            reliable: {noCost.slice(0, 5).map((r) => r.name).join(', ')}
            {noCost.length > 5 ? ` and ${noCost.length - 5} more` : ''}. They are shown as the custodian gives them and
            count in the totals. See <Anchor component={Link} size="sm" to="/admin/portfolios?tab=nocost">Portfolios → No cost</Anchor> for
            every such line across all portfolios.
          </Text>
        </Alert>
      )}
      {/* COST-SRC B: cyan = a figure entered in the app (app-language). */}
      {entered.length > 0 && (
        <Alert color="cyan" py={6} icon={<IconInfoCircle size={16} />}>
          <Text size="sm">
            <b>{entered.length} securit{entered.length === 1 ? 'y has' : 'ies have'} a cost entered in the app</b>, not
            given by the custodian: {entered.slice(0, 5).map((r) => r.name).join(', ')}
            {entered.length > 5 ? ` and ${entered.length - 5} more` : ''}. Their Avg purchase price, Total cost, Total
            P&amp;L, Capital P&amp;L, Currency P&amp;L and Total % P&amp;L use it, the last three recomputed by the
            custodian&apos;s own formulas (shaded cyan; hover for where it comes from). See or change it in{' '}
            <Anchor component={Link} size="sm" to="/admin/portfolios?tab=nocost">Portfolios → No cost</Anchor>.
          </Text>
        </Alert>
      )}

      <Group gap="sm" wrap="wrap">
        <Text size="sm">
          <b>{single ? `Portfolio ${pmetaByNo[single]?.label ?? splitRef(single).number}` : `${inView.length} portfolios`}</b>{' '}
          {/* Step 4 (P3): the portfolio's agreement, read-only (Admin assigns in Portfolios → Assign to…). */}
          {single && (pmetaByNo[single]?.agreement_id ? (
            <>· agreement{' '}
              <Anchor component={Link} to={`/agreements/${pmetaByNo[single]?.agreement_id}`} size="sm">
                {pmetaByNo[single]?.agreement_label}
              </Anchor>{' '}</>
          ) : <Text span size="sm" c="dimmed">· not linked to an agreement </Text>)}
          {!single && !manyPortfolios && (
            <Text span size="sm" c="dimmed">
              {inView.map((p) => (pmetaByNo[p] ? shortLabel(pmetaByNo[p]) : splitRef(p).number)).join(' · ')}
            </Text>
          )}
          {' '}· {positions} position{positions === 1 ? '' : 's'} ·{' '}
          {hasLoans ? (
            <Tooltip withArrow multiline w={320}
              label="Gross: every line but the loans; every % is a share of it. Net: with the loans.">
              <span>gross <b style={nonEur ? REF_MARK : undefined} title={nonEur ? refWhy(ref) : undefined}>{amount(grossRef)} {ref}</b> · net <b style={nonEur ? REF_MARK : undefined} title={nonEur ? refWhy(ref) : undefined}>{amount(netRef)} {ref}</b></span>
            </Tooltip>
          ) : <>total <b style={nonEur ? REF_MARK : undefined} title={nonEur ? refWhy(ref) : undefined}>{amount(grossRef)} {ref}</b></>}
        </Text>
        {stale.map((p) => (
          <Tooltip key={p.ref} withArrow multiline w={320}
            label={`Not current: portfolio ${p.label} has no positions on ${c.as_of}, so its latest known positions (${p.used_date}) are shown and counted in the Total. Click × to leave it out.`}>
            <Badge color="yellow" variant="filled" leftSection={<IconClock size={12} />}
              rightSection={<CloseButton size="xs" variant="transparent" c="dark" aria-label="Stop using"
                onClick={() => onSubstitute(p.ref, null)} />}
              style={{ textTransform: 'none' }}>
              {shortLabel(p)}: positions of {p.used_date}, not current
            </Badge>
          </Tooltip>
        ))}
        {/* Pedro 2026-10-23: the counter counts the not-approved positions (it counted the
            non-classified ones, which their group already shows). */}
        {nonApproved > 0 && (
          <Tooltip withArrow multiline w={340} label={nonText}>
            <Badge component={Link} to="/admin/securities?notApproved=1" color="yellow" variant="filled"
              leftSection={<IconAlertTriangle size={12} />} style={{ cursor: 'pointer', textTransform: 'none' }}>
              {nonApproved} position{nonApproved === 1 ? ' is' : 's are'} not approved
            </Badge>
          </Tooltip>
        )}
        <Group gap={10} wrap="nowrap" style={{ marginLeft: 'auto' }}>
          {ORIGIN_ORDER.map((k) => {
            const S = ORIGINS[k];
            return (
              <Group key={k} gap={3} wrap="nowrap">
                <S.icon size={13} style={{ color: ORIGIN_COLOR }} />
                <Text size="xs" c="dimmed">{S.short}</Text>
              </Group>
            );
          })}
        </Group>
        {sort && activeFilters === 0 && (
          <Anchor size="xs" onClick={() => setSort(null)}>Clear sort</Anchor>
        )}
      </Group>

      {/* Pedro 2026-10-24: the filter warnings as a banner, left justified (yellow: a warning,
          nothing blocked, app-language §2). TRADE-SORT (§3j): a trade never disappears silently
          behind a filter. */}
      {activeFilters > 0 && (
        <Alert color="yellow" py={6} icon={<IconAlertTriangle size={16} />}>
          <Group gap="md" wrap="wrap" justify="flex-start">
            <Text size="sm">
              <b>Filtered: {shown.length} of {allRows.length} lines.</b> The group subtotals and the totals cover only the
              lines shown; % of portfolio is always a share of the whole portfolio's gross value, or of the consolidated gross (loans left out).
            </Text>
            {addedHidden > 0 && (
              <Text size="sm">
                <b>{addedHidden} added securit{addedHidden === 1 ? 'y' : 'ies'} hidden by the filters.</b> {addedHidden === 1 ? 'It stays' : 'They stay'} in
                the draft and in the Buy / Sell / Net totals.
              </Text>
            )}
            <Anchor size="sm" onClick={() => { setFilters({}); setSort(null); }}>Clear filters and sort</Anchor>
          </Group>
        </Alert>
      )}

      <div ref={tableRef} style={{ overflow: 'auto', height, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: totalW + trade.width, tableLayout: 'fixed' }}>
          <thead>
            {twoLines && (
              <tr ref={head1Ref}>
                {visible.map((x, vi) => {
                  if (!x.group) return headCell(x, vi, 2, 0);
                  if (vi > 0 && visible[vi - 1].group === x.group && visible[vi - 1].base === x.base) return null;
                  let span = 1;
                  while (vi + span < visible.length && visible[vi + span].group === x.group && visible[vi + span].base === x.base) span++;
                  const w = visible.slice(vi, vi + span).reduce((s, y) => s + y.width, 0);
                  return (
                    <th key={`grp:${x.base}`} colSpan={span} {...drag.props(x.base)}
                      title={`${x.group}: a column per portfolio, then the Total. Drag to move.${inRef(x) ? ` ${refWhy(ref)}` : ''}`}
                      style={{ ...cellStyle(vi, inRef(x) ? REF_BG : 'var(--mantine-color-gray-1)', true, true, 0), width: w, minWidth: w, maxWidth: w,
                        height: HEAD1, textAlign: 'center', borderRight: GROUP_EDGE, ...drag.marker(x.base) }}>
                      {x.group}
                    </th>
                  );
                })}
                {trade.headCells(2, 0)}
              </tr>
            )}
            <tr>
              {visible.map((x, vi) => (twoLines ? (x.group ? headCell(x, vi, 1, head1H) : null) : headCell(x, vi, 1, 0)))}
              {!twoLines && trade.headCells(1, 0)}
            </tr>
          </thead>
          <tbody>
            {/* TRADE-1 3b-2 (Pedro: "Just below the header row, a new line opens, to input a new
                security"): the search, then each security added to buy — orange, staged until the
                draft is saved (app-language pattern 1). */}
            {trade.searchCell && (
              <tr>
                {visible.map((x, vi) => (
                  <td key={x.key} style={{ ...cellStyle(vi, 'var(--mantine-color-orange-0)', false), ...(vi === 0 ? { overflow: 'visible' } : {}) }}>
                    {vi === 0 ? trade.searchCell : ''}
                  </td>
                ))}
                {trade.blankCells('var(--mantine-color-orange-0)')}
              </tr>
            )}
            {/* M-REB-LOUD (Pedro's smoke 2026-10-26: "The row does not turn red"): an added row
                takes the trade's row shade too (red when it cannot be traded), orange otherwise. */}
            {addedShown.map((row) => {
              const bg = trade.rowShade(row) ?? 'var(--mantine-color-orange-0)';
              return [
                <tr key={`new:${row.key}`}>
                  {visible.map((x, vi) => (
                    <td key={x.key} style={cellStyle(vi, bg, false)}>
                      {x.cell ? x.cell(row) : x.text(row)}
                    </td>
                  ))}
                  {trade.rowCells(row, bg)}
                </tr>,
                ...subRowsOf(row, 0),
              ];
            })}
            {gridRows.map((g) => g.kind === 'group' ? (
              <tr key={`g:${g.node.id}`} onClick={() => toggleFold(g.node.id)} style={{ cursor: 'pointer' }}
                title={folded.has(g.node.id) ? 'Click to unfold' : 'Click to fold'}>
                {visible.map((x, vi) => (
                  <td key={x.key} style={cellStyle(vi, groupRowBg(g.node.depth), false, true)}>
                    {vi === 0 ? (
                      <span style={{ paddingLeft: g.node.depth * INDENT, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {folded.has(g.node.id) ? <IconChevronRight size={14} /> : <IconChevronDown size={14} />}
                        {g.node.label} <Text span size="xs" c="dimmed" fw={400}>({g.node.lineCount})</Text>
                      </span>
                    ) : summary(x, rowsOf(g.node))}
                  </td>
                ))}
                {trade.blankCells(groupRowBg(g.node.depth), true)}
              </tr>
            ) : [
              <tr key={`l:${g.row.key}`}>
                {visible.map((x, vi) => (
                  <td key={x.key} title={subWhy(x, g.row) ?? enteredWhy(x, g.row)} style={cellStyle(vi, subUsed(x, [g.row]) ? SUB_BG
                    : enteredWhy(x, g.row) ? ENTERED_BG
                    : trade.rowShade(g.row) ?? (g.i % 2 ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-body)'), false)}>
                    {vi === 0
                      ? <div style={{ paddingLeft: g.depth * INDENT + (g.depth ? 18 : 0), overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.cell ? x.cell(g.row) : x.text(g.row)}</div>
                      : (x.cell ? x.cell(g.row) : x.text(g.row))}
                  </td>
                ))}
                {trade.rowCells(g.row, trade.rowShade(g.row) ?? (g.i % 2 ? 'var(--mantine-color-gray-0)' : 'var(--mantine-color-body)'))}
              </tr>,
              ...subRowsOf(g.row, g.depth),
            ])}
            {gridRows.length === 0 && (
              <tr><td colSpan={visible.length} style={{ padding: 12 }}>
                <Text size="sm" c="dimmed">No line matches the filters.</Text>
              </td></tr>
            )}
            {/* HP-L: with loans in view, a Gross total (loans left out) above the Net total. */}
            {hasLoans && (
              <tr>
                {visible.map((x, vi) => (
                  <td key={x.key} title={inRef(x) ? refWhy(ref) : undefined}
                    style={{ ...cellStyle(vi, inRef(x) ? REF_BG : 'var(--mantine-color-gray-2)', false, true), position: 'sticky', bottom: netRowH * (trade.footRows.length + 1), zIndex: vi <= freezeIdx ? 2 : 1 }}>
                    {vi === 0
                      ? <><Tooltip withArrow label="Every line but the loans: the base of every %."><span>{activeFilters ? 'Gross total of the lines shown' : 'Gross total'}</span></Tooltip>{refTag}</>
                      : summary(x, shown.filter((r) => !r.is_loan))}
                  </td>
                ))}
                {trade.blankCells('var(--mantine-color-gray-2)', true, netRowH * (trade.footRows.length + 1))}
              </tr>
            )}
            <tr ref={netRowRef}>
              {visible.map((x, vi) => (
                <td key={x.key} title={inRef(x) ? refWhy(ref) : undefined}
                  style={{ ...cellStyle(vi, inRef(x) ? 'var(--mantine-color-yellow-3)' : 'var(--mantine-color-gray-3)', false, true), position: 'sticky', bottom: netRowH * trade.footRows.length, zIndex: vi <= freezeIdx ? 2 : 1 }}>
                  {vi === 0
                    ? <>{hasLoans
                      ? <Tooltip withArrow label="Every line, loans included. Its % is net ÷ gross."><span>{activeFilters ? 'Net total of the lines shown' : 'Net total'}</span></Tooltip>
                      : (activeFilters ? `Total of the ${shown.length} lines shown` : 'Total')}{refTag}</>
                    : summary(x, shown)}
                </td>
              ))}
              {trade.blankCells('var(--mantine-color-gray-3)', true, netRowH * trade.footRows.length)}
            </tr>
            {/* TRADE-1 (Pedro: "Three new rows appear at the bottom of the table, after the Total
                row: Buy total, Sell total, Net total"). Built like the Net row — a cell per column,
                every one sticky at the row's offset — so the values stay with their label when the
                table scrolls (TRADE-FOOT, Pedro's smoke 2026-10-23). */}
            {trade.footRows.map((f, i) => {
              const bottom = netRowH * (trade.footRows.length - 1 - i);
              return (
                <tr key={`trade:${f.label}`}>
                  {visible.map((x, vi) => (
                    <td key={x.key} style={{ ...cellStyle(vi, 'var(--mantine-color-gray-2)', false, true), position: 'sticky', bottom, zIndex: vi <= freezeIdx ? 2 : 1 }}>
                      {vi === 0 ? <Tooltip withArrow label={f.tip}><span>{f.label}{refTag}</span></Tooltip> : ''}
                    </td>
                  ))}
                  {f.cells(bottom)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Stack>
  );
}
